// Market orchestration: state container, the trade wrapper that applies hype,
// and the per-tick / per-day market step.

import { TUNABLES } from "@degen/shared/config/tunables";
import type { Rng } from "../rng";
import type { GameState } from "../state";
import { pushLog } from "../state";
import { createSentiment, sentimentNewDay, stepSentiment, type Sentiment } from "./sentiment";
import { executeBuy, executeSell, killToken, mcapOf, touchCandle, type Token, type TradeResult } from "./token";
import { runTokenScripts, scheduleSpawns, spawnNpcToken } from "./launcher";
import { npcDailyStipend, runNpcTraders } from "./npcTraders";
import type { Presale } from "./presale";

export interface Shill {
  tokenId: string;
  by: string; // "player" or "acct:N"
  tick: number;
  strength: number; // 0..1, how hard copytraders react
  bear?: boolean; // FUD: holders may dump instead of copytraders buying
}

export interface MarketStats {
  spawned: number;
  graduated: number;
  rugged: number;
  died: number;
}

export interface MarketState {
  tokens: Record<string, Token>;
  nextTokenId: number;
  sentiment: Sentiment;
  spawnTicks: number[]; // remaining scheduled NPC spawns for today
  shills: Shill[];
  presales: Presale[];
  stats: MarketStats;
}

const M = TUNABLES.market;

export function createMarket(): MarketState {
  return {
    tokens: {},
    nextTokenId: 1,
    sentiment: createSentiment(),
    spawnTicks: [],
    shills: [],
    presales: [],
    stats: { spawned: 0, graduated: 0, rugged: 0, died: 0 },
  };
}

export function liveTokens(m: MarketState): Token[] {
  const out: Token[] = [];
  for (const id in m.tokens) if (m.tokens[id].phase !== "dead") out.push(m.tokens[id]);
  return out;
}

function clampHype(h: number) {
  return h < 0 ? 0 : h > 100 ? 100 : h;
}

/** Buy through the market: executes the trade and applies hype + graduation events. */
export function marketBuy(state: GameState, t: Token, who: string, usd: number): TradeResult {
  const mcapBefore = Math.max(mcapOf(t), M.curve.vUsd0);
  const r = executeBuy(t, who, usd, state.tick);
  if (!r.ok) return r;
  if (!t.rugged) t.hype = clampHype(t.hype + (M.hype.buyImpact * r.usd) / mcapBefore);
  if (r.graduated) {
    state.market.stats.graduated++;
    pushLog(state, "graduated", `$${t.ticker} graduated to the AMM`, { tokenId: t.id });
  }
  return r;
}

export function marketSell(state: GameState, t: Token, who: string, tokens: number): TradeResult {
  const mcapBefore = Math.max(mcapOf(t), M.curve.vUsd0);
  const r = executeSell(t, who, tokens, state.tick);
  if (!r.ok) return r;
  t.hype = clampHype(t.hype - (M.hype.sellImpact * r.usd) / mcapBefore);
  return r;
}

export function addShill(m: MarketState, s: Shill) {
  m.shills.push(s);
}

export function marketTick(state: GameState, rngMarket: Rng, rngNpc: Rng) {
  const m = state.market;
  const tick = state.tick;

  if (tick % M.sentiment.stepEveryTicks === 0) stepSentiment(m.sentiment, rngMarket);

  while (m.spawnTicks.length && m.spawnTicks[0] <= tick) {
    m.spawnTicks.shift();
    spawnNpcToken(state, rngMarket, tick);
  }

  const live = liveTokens(m);
  for (const t of live) touchCandle(t, tick);

  runTokenScripts(state, rngMarket, live);
  runNpcTraders(state, rngNpc, live);

  if (tick % 60 === 0) {
    checkDeaths(state, live);
    const minTick = tick - M.shillTtlTicks;
    if (m.shills.length && m.shills[0].tick < minTick) m.shills = m.shills.filter((s) => s.tick >= minTick);
  }
}

function checkDeaths(state: GameState, live: Token[]) {
  for (const t of live) {
    if (mcapOf(t) >= M.deadMcap) {
      t.lowSinceTick = null;
      continue;
    }
    if (t.lowSinceTick === null) t.lowSinceTick = state.tick;
    else if (state.tick - t.lowSinceTick >= M.deadAfterTicks) {
      killToken(t, state.tick);
      state.market.stats.died++;
      pushLog(state, "token_died", `$${t.ticker} is dead. RIP.`, { tokenId: t.id });
    }
  }
}

export function marketNewDay(state: GameState, rngMarket: Rng, rngNpc: Rng, day: number) {
  const m = state.market;
  const shift = sentimentNewDay(m.sentiment, day, rngMarket);
  if (shift === "bull") pushLog(state, "regime", "Bull run! Everything is green and everyone is a genius.");
  if (shift === "crash") pushLog(state, "regime", "Market crash. Blood in the trenches.");
  scheduleSpawns(state, rngMarket);
  npcDailyStipend(state.npcs, rngNpc);
  pruneDeadTokens(state);
}

/** Drop long-dead tokens the player doesn't hold, and trim their buffers. */
function pruneDeadTokens(state: GameState) {
  const m = state.market;
  const keepTicks = M.deadKeepDays * TUNABLES.clock.ticksPerDay;
  for (const id in m.tokens) {
    const t = m.tokens[id];
    if (t.phase !== "dead") continue;
    if (t.candles.length > 60) t.candles = t.candles.slice(-60);
    t.trades = [];
    const playerHolds = (t.holders["player"] ?? 0) > 0 || t.creator === "player";
    if (playerHolds) {
      // Keep only the player's ledger entry; NPC bags of dead coins don't matter.
      t.holders = t.holders["player"] ? { player: t.holders["player"] } : {};
      continue;
    }
    if (t.diedTick !== undefined && state.tick - t.diedTick >= keepTicks) delete m.tokens[id];
  }
}
